'use client';

import { useFormValidation } from '@app/_shared/hooks/useFormValidation';
import { GoogleSignUpButton } from '@app/_shared/sign-up/GoogleSignUpButton';
import { SignUpContextProvider, useSignUpContext } from '@app/_shared/sign-up/SignUpContext';
import { useSubmitSignUpForm } from '@app/_shared/sign-up/useSubmitSignUpForm';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { type JSX, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@videoblocks/design-system';
import { View, ViewOff } from '@videoblocks/react-icons';

import { PASSWORD_MIN_LENGTH } from '../../../../../../lib/validation/passwordValidation';

const REDIRECT_AFTER_REGISTER = 'ai/conv-search/new';

/** Shared ID for the email input — used by DemoChat to focus this field on upgrade click */
export const SIGNUP_EMAIL_INPUT_ID = 'conv-search-email';
const SIGNUP_PASSWORD_INPUT_ID = 'conv-search-password';
const SIGNUP_AGREEMENT_CHECKBOX_ID = 'conv-search-agreement-checkbox';

const INPUT_CLASS =
    'w-full border rounded py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500';

// after a user signs up, redirect them back to a chat session, with any prompt param (will auto-kick off a chat).
// Prefers a prompt passed in (from the visitor's typed query) over the URL ?prompt param.
const useGetRedirectUrl = (initialPrompt: string | null): string => {
    const urlParams = useSearchParams();
    const prompt = (initialPrompt ?? urlParams.get('prompt') ?? '').trim();
    return prompt ? `${REDIRECT_AFTER_REGISTER}?prompt=${encodeURIComponent(prompt)}` : REDIRECT_AFTER_REGISTER;
};

function SignUpForm({ initialPrompt }: { initialPrompt: string | null }): JSX.Element {
    const { error, submitText } = useSignUpContext();
    const [showPassword, setShowPassword] = useState(false);
    const { isFormValid, errors, touched, refs, handleInputChange, handleBlur, handleFocus } = useFormValidation();
    const redirectUrl = useGetRedirectUrl(initialPrompt);
    const handleSubmit = useSubmitSignUpForm(undefined, redirectUrl);

    return (
        <form onSubmit={handleSubmit} aria-label="Registration Form" className="flex flex-col gap-4">
            {error && (
                <div className="p-2 bg-red-50 border border-red-200 rounded py-2 px-3">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            <div className="flex flex-col gap-1">
                <label htmlFor={SIGNUP_EMAIL_INPUT_ID} className="text-sm font-bold text-gray-900">
                    Email
                </label>
                <input
                    ref={refs.emailInputRef}
                    id={SIGNUP_EMAIL_INPUT_ID}
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    autoComplete="email"
                    required
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('email')}
                    onBlur={() => handleBlur('email')}
                    className={twMerge(
                        INPUT_CLASS,
                        errors.email && touched.email ? 'border-red-500' : 'border-gray-400',
                    )}
                />
                {touched.email && errors.email && <div className="mt-1 text-sm text-red-600">{errors.email}</div>}
            </div>

            <div className="flex flex-col gap-1">
                <label htmlFor={SIGNUP_PASSWORD_INPUT_ID} className="text-sm font-bold text-gray-900">
                    Password
                </label>
                <div className="relative">
                    <input
                        // eslint-disable-next-line react-hooks/refs -- ref originates from useFormValidation, not created locally
                        ref={refs.passwordInputRef}
                        id={SIGNUP_PASSWORD_INPUT_ID}
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
                        autoComplete="new-password"
                        required
                        minLength={PASSWORD_MIN_LENGTH}
                        onChange={handleInputChange}
                        onFocus={() => handleFocus('password')}
                        onBlur={() => handleBlur('password')}
                        className={twMerge(
                            INPUT_CLASS,
                            'pr-10',
                            errors.password && touched.password ? 'border-red-500' : 'border-gray-400',
                        )}
                    />
                    <Tooltip
                        title={showPassword ? 'Hide password' : 'Show password'}
                        trigger={
                            <button
                                type="button"
                                onClick={() => setShowPassword((prev) => !prev)}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset focus:text-gray-600"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <ViewOff className="h-5 w-5" /> : <View className="h-5 w-5" />}
                            </button>
                        }
                    />
                </div>
                {touched.password && errors.password && (
                    <div className="mt-1 text-sm text-red-600">{errors.password}</div>
                )}
            </div>

            <div>
                <div className="flex items-start">
                    <input
                        // eslint-disable-next-line react-hooks/refs -- ref originates from useFormValidation, not created locally
                        ref={refs.checkboxRef}
                        id={SIGNUP_AGREEMENT_CHECKBOX_ID}
                        name="agreement-checkbox"
                        type="checkbox"
                        required
                        onChange={handleInputChange}
                        onFocus={() => handleFocus('checkbox')}
                        onBlur={() => handleBlur('checkbox')}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={SIGNUP_AGREEMENT_CHECKBOX_ID} className="ml-3 text-sm text-gray-600">
                        I have read and accept the{' '}
                        <Link className="text-blue-700 underline" href="/terms-of-service">
                            Terms of Service
                        </Link>
                        ,{' '}
                        <Link className="text-blue-700 underline" href="/privacy">
                            Privacy Policy
                        </Link>
                        , and the License Agreement that applies to my plan:{' '}
                        <Link className="text-blue-700 underline" href="/license/individual-license">
                            Individual License
                        </Link>{' '}
                        /{' '}
                        <Link className="text-blue-700 underline" href="/license/business-license">
                            Business License
                        </Link>
                    </label>
                </div>
                {touched.checkbox && errors.checkbox && (
                    <div className="mt-1 text-sm text-red-600">{errors.checkbox}</div>
                )}
            </div>

            <div className="flex flex-col items-center gap-4">
                <button
                    type="submit"
                    disabled={!isFormValid}
                    className={twMerge(
                        'w-full py-2.5 px-6 rounded-lg text-sm font-semibold transition-colors cursor-pointer',
                        isFormValid
                            ? 'bg-yellow-400 hover:bg-yellow-600 text-gray-900'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed',
                    )}
                >
                    {submitText}
                </button>

                <p className="text-sm text-gray-900">
                    Already have an account?{' '}
                    <Link
                        className="text-blue-700 hover:underline cursor-pointer"
                        href={`/login?redirectAfterLogin=${encodeURIComponent(redirectUrl)}`}
                    >
                        Sign in
                    </Link>
                </p>
            </div>
        </form>
    );
}

/**
 * Sign-up modal displayed to visitors on the conv-search page.
 * Positioned over the results pane with the same form as the main sign-up page.
 */
export function SignUpModal({ initialPrompt }: { initialPrompt: string | null }): JSX.Element {
    const redirectUrl = useGetRedirectUrl(initialPrompt);
    return (
        <div className="fixed inset-0 z-10 flex items-center justify-center lg:absolute">
            <div className="flex flex-col gap-10 bg-white rounded-lg w-full max-w-md mx-4 p-8 shadow-2xl">
                <div className="flex flex-col gap-4">
                    <h2 className="text-2xl font-bold leading-tight text-gray-900">
                        Create an account to unlock the full experience
                    </h2>
                    <p className="text-base text-gray-700">Create a free account. No credit card needed.</p>
                </div>

                <SignUpContextProvider>
                    <div className="flex flex-col gap-4">
                        <GoogleSignUpButton redirectAfterRegister={redirectUrl} />

                        <div className="flex items-center gap-2">
                            <div className="flex-1 h-px bg-gray-300" />
                            <span className="text-xs font-semibold text-gray-400">or</span>
                            <div className="flex-1 h-px bg-gray-300" />
                        </div>

                        <SignUpForm initialPrompt={initialPrompt} />
                    </div>
                </SignUpContextProvider>
            </div>
        </div>
    );
}
